import { apiService, ApiResponse } from './api';
import { BlogPost, BlogCategory, BlogImage, BlogListResponse, BlogFilters } from '../types/blog';

class BlogService {
  // Blog Posts
  async getBlogPosts(filters: BlogFilters = {}): Promise<ApiResponse<BlogListResponse>> {
    const params = new URLSearchParams();
    
    if (filters.status && filters.status !== 'all') params.append('status', filters.status);
    if (filters.category) params.append('category', filters.category);
    if (filters.search) params.append('search', filters.search);
    if (filters.sortBy) params.append('sortBy', filters.sortBy);
    if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());

    const queryString = params.toString();
    const endpoint = `/api/admin/blog/posts${queryString ? `?${queryString}` : ''}`;
    
    return apiService.get<BlogListResponse>(endpoint);
  }

  async getBlogPost(id: string): Promise<ApiResponse<BlogPost>> {
    return apiService.get<BlogPost>(`/api/admin/blog/posts/${id}`);
  }

  async createBlogPost(data: Partial<BlogPost>): Promise<ApiResponse<BlogPost>> {
    return apiService.post<BlogPost>('/api/admin/blog/posts', data);
  }

  async updateBlogPost(id: string, data: Partial<BlogPost>): Promise<ApiResponse<BlogPost>> {
    return apiService.patch<BlogPost>(`/api/admin/blog/posts/${id}`, data);
  }

  async deleteBlogPost(id: string): Promise<ApiResponse<void>> {
    return apiService.delete<void>(`/api/admin/blog/posts/${id}`);
  }

  async publishBlogPost(id: string): Promise<ApiResponse<BlogPost>> {
    return apiService.post<BlogPost>(`/api/admin/blog/posts/${id}/publish`, {});
  }

  async unpublishBlogPost(id: string): Promise<ApiResponse<BlogPost>> {
    return apiService.post<BlogPost>(`/api/admin/blog/posts/${id}/unpublish`, {});
  }

  // Categories
  async getCategories(): Promise<ApiResponse<BlogCategory[]>> {
    return apiService.get<BlogCategory[]>('/api/admin/blog/categories');
  }

  async createCategory(data: Partial<BlogCategory>): Promise<ApiResponse<BlogCategory>> {
    return apiService.post<BlogCategory>('/api/admin/blog/categories', data);
  }

  async updateCategory(id: string, data: Partial<BlogCategory>): Promise<ApiResponse<BlogCategory>> {
    return apiService.patch<BlogCategory>(`/api/admin/blog/categories/${id}`, data);
  }

  async deleteCategory(id: string): Promise<ApiResponse<void>> {
    return apiService.delete<void>(`/api/admin/blog/categories/${id}`);
  }

  // Images
  async getImages(): Promise<ApiResponse<BlogImage[]>> {
    return apiService.get<BlogImage[]>('/api/admin/blog-images');
  }

  async uploadImage(file: File, altText?: string): Promise<ApiResponse<BlogImage>> {
    const formData = new FormData();
    formData.append('image', file);
    if (altText) formData.append('altText', altText);

    try {
      // Get auth token - use admin-token for authenticated requests
      let token = 'admin-token';
      const storedUser = localStorage.getItem('adminUser');
      if (storedUser) {
        // User is logged in, use admin token
        token = 'admin-token';
      }
      
      // Custom request for file upload with auth
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3002'}/api/admin/blog-images/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        return {
          success: false,
          error: {
            code: 'UPLOAD_ERROR',
            message: errorData.message || `Upload failed: ${response.statusText}`,
          },
        };
      }

      const data = await response.json();
      return { success: true, data: data.data || data };
    } catch (error) {
      return {
        success: false,
        error: {
          code: 'NETWORK_ERROR',
          message: error instanceof Error ? error.message : 'Network error during upload',
        },
      };
    }
  }

  async deleteImage(id: string): Promise<ApiResponse<void>> {
    return apiService.delete<void>(`/api/admin/blog-images/${id}`);
  }
}

export const blogService = new BlogService();